import api from "../api/api";

const baseURL = import.meta.env.VITE_API_BASE_URL;


export interface Report {
  id: string;
  report_no: string;
  report_name: string;
  name?: string;
  country: string;
  country_name?: string;
  country_code?: string;
  original_name?: string;
  status: string;
  timestamp: string;
  created_at?: string;
  download_url?: string;
  blob_name?: string;
  filesize?: number;
  username?: string;
  email?: string;
  clients_name?: string;
  client_name?: string;
  input_file_name: string;
  organization: string;
  organization_name: string;
}
export interface PeerGroupReport {
  id: number;
  bank_name: string;
  country: string;
}

export interface DownloadResult {
  report: Report;
  success: boolean;
  error?: string;
}

export interface ApiError {
  response?: { data?: unknown };
  message: string;
  name?: string;
  code?: string;
}

// Add this to your existing reports service file

export interface BulkFileData {
  file: File;
  id: string;
  reportName: string;
  organization: string;
  country: string;
  currency: string;
  rate: string,
}

export async function handleProcessPdfFile(
  formData: FormData,
  access_token: string
): Promise<any> {
  if (!formData.get("file")) throw new Error("No file provided");
  
  try {
    const config: Record<string, any> = {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${access_token}`,
      },
    };

    const response = await api.post("/api/v1/reports/pdf/extract/", formData, config);
    return response.data;
  } catch (error: any) {
    if (
      error.name === "CanceledError" ||
      error.name === "AbortError" ||
      error.code === "ERR_CANCELED"
    ) {
      console.log("Request was cancelled by user");
      throw new Error("Request cancelled by user");
    }

    console.error("Error uploading file:", error);
    throw error;
  }
}

export interface CachedCommentaryPreview {
  exists: boolean;
  financial_year?: number;
  financial_month?: number;
  currency?: string;
  columns?: string[];
  rows?: { key: string; label: string; values: string[] }[];
}

// Check whether a saved (admin pre-generated) commentary exists for the
// selected organization + sector + sub-sector. Returns the financial
// highlights table (values only — no commentary text) for preview.
export async function checkCachedCommentary(
  organizationId: string,
  sectorId: string,
  subSectorId: string,
  access_token: string,
  currency?: string
): Promise<CachedCommentaryPreview> {
  try {
    const response = await api.get<CachedCommentaryPreview>(
      "/api/v1/reports/cached/",
      {
        params: {
          organization_id: organizationId,
          sector_id: sectorId,
          sub_sector_id: subSectorId,
          ...(currency ? { currency } : {}),
        },
        headers: { Authorization: `Bearer ${access_token}` },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error("Error checking cached commentary:", error);
    return { exists: false };
  }
}

export async function handleProcessReportFile(
  formData: FormData,
  userId: string,
  signal: AbortSignal | null = null,
  access_token: string
): Promise<any> {
  const useCached = formData.get("use_cached") === "true";
  if (!useCached && !formData.get("file")) throw new Error("No file provided");
  console.log(userId);

  try {
    const config: Record<string, any> = {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${access_token}`,
      },
    };

    if (signal) {
      config.signal = signal;
    }

    const response = await api.post("/api/v1/reports/process/", formData, config);
    return response.data;
  } catch (error: any) {
    if (
      error.name === "CanceledError" ||
      error.name === "AbortError" ||
      error.code === "ERR_CANCELED"
    ) {
      console.log("Request was cancelled by user");
      throw new Error("Request cancelled by user");
    }

    console.error("Error uploading file:", error);
    throw error;
  }
}

export async function handleGetReports(access_token: string): Promise<Report[]> {
  try {
    const response = await api.get<Report[]>("/api/v1/reports/my/?own=true", {
      headers: { Authorization: `Bearer ${access_token}` },
    });

    return response.data.map((report) => ({
      id: report.id,
      report_no: report.report_no,
      report_name: report.report_name,
      country: report.country,
      fileName: report.original_name,
      status: report.status,
      timestamp: report.created_at || '',
      download_url: report.download_url,
      blob_name: report.blob_name,
      filesize: report.filesize,
      username: report.username,
      input_file_name: report.input_file_name,
      clients_name: report.clients_name,
      organization: report.organization,
      organization_name: report.organization_name || ''
    }));
  } catch (error: any) {
    console.error("Error fetching reports:", error);
    throw error;
  }
}

// export async function handleGetReportsByUser(
//   userId: string,
//   access_token: string
// ): Promise<Report[]> {
//   try {
//     const response = await api.get<Report[]>(`/api/reports/reports/user/${userId}`, {
//       headers: { Authorization: `Bearer ${access_token}` },
//     });

//     return response.data.map((report) => ({
//       id: report.id,
//       report_no: report.report_no,
//       report_name: report.report_name,
//       fileName: report.original_name,
//       status: report.status,
//       timestamp: report.timestamp,
//       blob_name: report.blob_name,
//       input_file_name: report.input_file_name
//     }));
//   } catch (error: any) {
//     console.error("Error fetching reports:", error);
//     throw error;
//   }
// }

export async function handleGetReportsByUser(
  userId: string,
  access_token: string,
  own: boolean = false,
  status?: string,
  client?: string,
  country?: string,
  bank?: string,
  startDate?: string,
  endDate?: string,
  searchTerm?: string,
  limit?: number,
  offset?: number
): Promise<{ data: Report[], total: number }> {
  try {
    const params = new URLSearchParams();
    if (own) params.append("own", "true");
    if (status && status !== "all") params.append("status", status);
    if (country && country !== "all") params.append("country", country);
    if (bank && bank !== "all") params.append("organization", bank);
    if (startDate) {
      const formattedStart = startDate.length === 7 ? `${startDate}-01` : startDate;
      params.append("start_date", formattedStart);
    }
    if (endDate) {
      let formattedEnd = endDate;
      if (endDate.length === 7) {
        const [year, month] = endDate.split('-');
        const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
        formattedEnd = `${endDate}-${lastDay}`;
      }
      params.append("end_date", formattedEnd);
    }
    if (searchTerm) params.append("search", searchTerm);
    if (limit) params.append("page_size", limit.toString());
    if (offset) params.append("page", Math.floor(offset / (limit || 10) + 1).toString());

    const response = await api.get<any>(`/api/v1/reports/my/?${params.toString()}`, {
      headers: { Authorization: `Bearer ${access_token}` },
    });

    // Handle DRF paginated response
    const data = response.data.results || response.data.data || [];
    const total = response.data.count || response.data.total || data.length;

    const mappedData = data.map((report: any) => ({
      id: report.id.toString(),
      report_no: report.report_no,
      report_name: report.name || report.original_name || "Untitled Report",
      name: report.name,
      country: report.country_name || report.country_code || report.country?.iso2 || "Unknown",
      country_name: report.country_name,
      country_code: report.country_code,
      original_name: report.upload_file?.original_name || report.original_name,
      status: report.status,
      created_at: report.created_at,
      updated_at: report.updated_at,
      download_url: report.download_url,
      input_download_url: report.input_download_url,
      organization: report.organization_name || report.organization || "Unknown",
      organization_name: report.organization_name || "",
      client_name: report.client_name,
      username: report.username,
      email: report.email,
      currency: report.currency,
      blob_name: report.blob_name,
      size: report.size,
      json_data: report.json_data,
      upload_file: report.upload_file,
      client: report.client,
      user: report.user,
    }));

    return {
      data: mappedData,
      total: total
    };
  } catch (error: any) {
    console.error("Error fetching reports:", error);
    throw error;
  }
}

export async function handleGetBanksHasReportByUser(
  _userId: string,
  country: string,
  access_token: string,
  own: boolean = false,
  page: number = 1,
  pageSize: number = 10,
  hasFinancials: boolean = false
): Promise<{ data: PeerGroupReport[], total: number }> {
  try {
    // Build query params for organizations endpoint
    const params = new URLSearchParams();
    if (country && country !== "all") {
      params.append("country", country);
    }
    if (own) {
      params.append("own", "true");
    }
    if (hasFinancials) {
      params.append("has_financials", "true");
    }
    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());
    const queryString = params.toString();
    const url = `/api/v1/organizations/${queryString ? `?${queryString}` : ""}`;

    const response = await api.get<any>(
      url,
      { headers: { Authorization: `Bearer ${access_token}` } }
    );

    // Handle DRF paginated response
    const data = response.data.results || response.data.data || response.data;
    const total = response.data.count || response.data.data?.count || response.data.total || (Array.isArray(data) ? data.length : 0);
    
    if (Array.isArray(data)) {
      return {
        data: data.map((org: any) => ({
          id: org.id,
          bank_name: org.name,
          country: org.country?.iso2 || (country !== "all" ? country : org.country_code)
        })),
        total
      };
    }
    
    return { data: [], total: 0 };
  } catch (error: any) {
    console.error("Error fetching banks:", error);
    return { data: [], total: 0 };
  }
}

export async function handleGetReportById(
  id: string,
  access_token: string
): Promise<Report | null> {
  try {
    const response = await api.get<Report>(`/api/v1/reports/${id}/`, {
      headers: { Authorization: `Bearer ${access_token}` },
    });

    if (response.data) {
      return {
        ...response.data,
        timestamp: response.data.created_at || '',
        organization: response.data.organization_name || response.data.organization || '',
        organization_name: response.data.organization_name || '',
        country: response.data.country_name || response.data.country_code || '',
        country_name: response.data.country_name || '',
        report_name: response.data.name || response.data.report_name || 'Untitled Report',
        name: response.data.name || response.data.report_name
      };
    }
    return null;
  } catch (error: any) {
    console.error("Error fetching report:", error);
    throw error;
  }
}

export async function downloadOutputFile(
  reportId: string,
  userId: string,
  fileName: string,
  accessToken: string
): Promise<void> {
  try {
    if (!reportId) throw new Error("Invalid report id");

    const response = await fetch(
      `${baseURL}/api/v1/reports/${reportId}/download/`,
      {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch file: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const blob = await response.blob();

    // Try to get filename from server headers
    const contentDisposition = response.headers.get("Content-Disposition");
    let finalName = fileName || "report.docx";
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?(.+)"?/);
      if (match?.[1]) finalName = match[1];
    }

    // Ensure .docx extension
    if (!finalName.endsWith(".docx")) {
      finalName = finalName.replace(/\.[^/.]+$/, "") + ".docx";
    }

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = finalName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (err: any) {
    console.error(`Error downloading output report ${reportId}:`, err);
    // Re-throw the error so the calling function can handle it
    throw new Error(`Failed to download report ${reportId}: ${err.message}`);
  }
}

export async function downloadPeerGroupOutputFile(
  banks: number[],
  userId: string,
  country: string,
  currency: string,
  currencyRate: string,
  convertIn: string,
  accessToken: string,
  own: boolean = false
): Promise<void> {
  try {
    if (!banks || banks.length === 0) throw new Error("Invalid banks");

    const payload: any = {
      country: country,
      currency: currency,
      banks: banks,
      convert_in: convertIn,
      own: own
    };

    // Only include fx_rate_id if it's not "none" or empty
    if (currencyRate && currencyRate !== "none") {
      payload.fx_rate_id = currencyRate;
    }

    const response = await fetch(
      `${baseURL}/api/v1/peer-analysis/download/`,
      {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${accessToken}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errorJson = JSON.parse(errorText);
        if (errorJson.message === "No financial data found." || errorJson.errors?.detail === "No financial data found.") {
          throw new Error("No financial data found for the selected banks. Please try different banks or check if data is available.");
        }
      } catch (e) {
        // If JSON parsing fails, use the original error text
      }
      throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
    }

    const blob = await response.blob();

    const contentDisposition = response.headers.get("Content-Disposition");
    let finalName = "peer_analysis.xlsx";
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?(.+)"?/);
      if (match?.[1]) finalName = match[1];
    }

    if (!finalName.endsWith(".xlsx")) {
      finalName = finalName.replace(/\.[^/.]+$/, "") + ".xlsx";
    }

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = finalName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error(`Error downloading peer analysis:`, error);
    throw error;
  }
}

export async function downloadMemoOutputFile(
  banks: number[],
  userId: string,
  country: string,
  accessToken: string,
  own: boolean = false
): Promise<void> {
  try {
    if (!banks || banks.length === 0) throw new Error("Invalid banks");

    const payload = {
      country: country,
      organization_ids: banks,
      own: own
    };

    const response = await fetch(
      `${baseURL}/api/v1/country-memo/generate/`,
      {
        method: "POST",
        headers: { 
          "Authorization": `Bearer ${accessToken}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      if (response.status === 500) {
        throw new Error("Something went wrong. Please try again later.");
      }
      throw new Error(`Failed to fetch file: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    const actualData = data.data || data;

    const link = document.createElement("a");
    link.href = actualData.download_url;
    link.download = actualData.file_name || "country_memo.docx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (err: any) {
    console.error(`Error downloading report:`, err);
    throw new Error(`Failed to download report: ${err.message}`);
  }
}

export async function downloadInputFile(
  reportId: string,
  fileName: string,
  access_token: string
): Promise<void> {
  try {
    if (!reportId) throw new Error("Invalid report id");

    const response = await fetch(
      `${baseURL}/api/v1/reports/${reportId}/download-input/`,
      {
        method: "GET",
        headers: { Authorization: `Bearer ${access_token}` },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch file: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || "input_file";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (err: any) {
    console.error(`Error downloading input file:`, err);
    throw new Error(`Failed to download input file: ${err.message}`);
  }
}

export async function handleDeleteReport(
  reportId: string,
  access_token: string
): Promise<any> {
  try {
    const response = await api.delete(`/api/v1/reports/${reportId}`, {
      headers: { Authorization: `Bearer ${access_token}` },
    });
    return response.data;
  } catch (error: any) {
    console.error("Error deleting report:", error);
    throw error;
  }
}

export async function downloadReportDirect(downloadUrl: string): Promise<void> {
  try {
    window.open(downloadUrl, "_blank");
  } catch (error: any) {
    console.error("Error downloading report:", error);
  }
}

export async function generateReportDownloadUrl(
  reportId: string,
  access_token: string
): Promise<string> {
  try {
    const response = await api.get<Report>(`/api/v1/reports/reports/${reportId}`, {
      headers: { Authorization: `Bearer ${access_token}` },
    });
    return response.data.download_url || "";
  } catch (error: any) {
    console.error("Error generating download URL:", error);
    throw error;
  }
}

export async function fetchBulkProgress(
  userId: string,
  accessToken: string
): Promise<{ total: number; completed: number; failed: number; finished?: boolean }> {
  console.warn("fetchBulkProgress is not implemented in the current backend.");
  return { total: 0, completed: 0, failed: 0, finished: true };
}

export async function handleBulkProcessReportFiles(
  filesData: BulkFileData[],
  userId: string,
  reportType: string,
  reportType2: string,
  sector_id: string,
  sub_sector_id: string,
  convert_in: string,
  signal: AbortSignal,
  accessToken: string
) {
  console.warn("handleBulkProcessReportFiles is not implemented in the current backend.");
  throw new Error("Bulk processing is not supported by the current backend.");
}

